import React, { useRef, useState, useEffect } from 'react';
import { Video, Upload, Pause, Play, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { videoService } from '../services/videoService';
import useAuth from '../hooks/stores/useAuth';

export default function VideoUpload() {
  const fileInput = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [resumeData, setResumeData] = useState(null);
  const [uploadCancelToken, setUploadCancelToken] = useState(null);
  const [uploadPaused, setUploadPaused] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Get interviewer ID from auth
  const { userId } = useAuth();

  // Load resume data and uploaded videos from localStorage on component mount
  useEffect(() => {
    const savedResumeData = localStorage.getItem('resumeUploadData');
    if (savedResumeData) {
      setResumeData(JSON.parse(savedResumeData));
    }

    const savedUploadedVideos = localStorage.getItem('uploadedVideos');
    if (savedUploadedVideos) {
      setUploadedVideos(JSON.parse(savedUploadedVideos));
    }
  }, []);

  const saveResumeData = (data) => {
    localStorage.setItem('resumeUploadData', JSON.stringify(data));
    setResumeData(data);
  };

  const clearResumeData = () => {
    localStorage.removeItem('resumeUploadData');
    setResumeData(null);
    setUploadProgress(0);
  };

  const saveUploadedVideos = (videos) => {
    localStorage.setItem('uploadedVideos', JSON.stringify(videos));
  };

  // This function is designed to query the Google Cloud Storage (GCS) 
  // resumable upload endpoint to determine how many bytes of a file have already been uploaded.
  const getUploadStatus = async (uploadUrl) => {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Range': 'bytes */*'
        }
      });
      
      if (response.status === 308) {
        const range = response.headers.get('Range');
        if (range) {
          const uploadedBytes = parseInt(range.split('-')[1]) + 1;
          return uploadedBytes;
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const uploadFileToGCS = async (file, resumeFromByte = 0, existingUploadUrl = null) => {
    let uploadUrl = existingUploadUrl;
    let currentResumeData = resumeData;
    let fullFilename = null; // Store the full filename with folder path
    
    try {
      if (!uploadUrl) {
        const response = await videoService.getResumableUploadUrl(file.name, userId);
        const { url, filename } = response;
        
        // Store the full filename returned from the API (includes folder path)
        fullFilename = filename || file.name;
        
        const sessionRes = await fetch(url, {
          method: 'POST',
          headers: {
            'x-goog-resumable': 'start',
            'Content-Type': 'video/mp4'
          }
        });
        
        uploadUrl = sessionRes.headers.get('Location');
        
        currentResumeData = {
          file: {
            name: file.name,
            size: file.size,
            type: file.type
          },
          uploadUrl: uploadUrl,
          uploadedBytes: resumeFromByte,
          fullFilename: fullFilename // Store the full filename in resume data
        };
        saveResumeData(currentResumeData);
      } else {
        // If resuming, get the full filename from resume data
        fullFilename = currentResumeData?.fullFilename || file.name;
      }

      const cancelToken = axios.CancelToken.source();
      setUploadCancelToken(cancelToken);

      const fileSlice = resumeFromByte > 0 ? file.slice(resumeFromByte) : file;
      const totalSize = file.size;
      
      await axios.put(uploadUrl, fileSlice, {
        headers: {
          'Content-Type': 'video/mp4',
          ...(resumeFromByte > 0 && {
            'Content-Range': `bytes ${resumeFromByte}-${totalSize - 1}/${totalSize}`
          })
        },
        onUploadProgress: (progressEvent) => {
          const uploadedBytes = resumeFromByte + progressEvent.loaded;
          const percent = Math.round((uploadedBytes * 100) / totalSize);
          setUploadProgress(percent);
          
          const updatedResumeData = {
            ...currentResumeData,
            uploadedBytes: uploadedBytes
          };
          saveResumeData(updatedResumeData);
          currentResumeData = updatedResumeData;
        },
        cancelToken: cancelToken.token
      });

      setUploadProgress(100);
      setIsProcessing(true); // Start processing state
      
      // Update video status after successful upload
      let videoStatusResponse = null;
      try {
        videoStatusResponse = await videoService.updateVideoStatus(fullFilename);
      } catch (statusError) {
        console.error('Failed to update video status:', statusError);
      }
      
      // Only clear resume data after status update is complete
      clearResumeData();
      setIsProcessing(false);
      
      // Return the full filename, streaming URL, and status response
      const streamUrl = videoService.getVideoStreamUrl(fullFilename);
      return { filename: fullFilename, url: streamUrl, statusResponse: videoStatusResponse };
    } catch (error) {
      setIsProcessing(false); // Reset processing state on error
      if (axios.isCancel(error)) {
        // Check if it was cancelled vs paused
        if (error.message === 'Upload cancelled by user') {
          // Don't set paused state for cancellation
          throw new Error('Upload cancelled');
        } else {
          setUploadPaused(true);
          throw new Error('Upload paused');
        }
      } else {
        throw error;
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadPaused(false);
      
      if (resumeData && resumeData.file.name === file.name && resumeData.file.size === file.size) {
        const percent = Math.round((resumeData.uploadedBytes * 100) / resumeData.file.size);
        setUploadProgress(percent);
      } else {
        clearResumeData();
        setUploadProgress(0);
      }
    }
  };

  const uploadVideo = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    setUploadPaused(false);

    try {
      const result = await uploadFileToGCS(selectedFile);
      
      if (result.url) {
        navigator.clipboard.writeText(result.url).catch(err => console.error('Failed to copy URL:', err));
        toast.success('Upload Successful!');
      } else {
        toast.success('Video uploaded successfully!');
      }
      
      // Only add to localStorage if user_id is null (video not assigned)
      const statusResponse = result.statusResponse;
      
      // Check if user_id is null (access from data property if response is nested)
      const userData = statusResponse?.data || statusResponse;
      
      if (userData && userData.user_id === null) {
        const newVideo = {
          filename: result.filename,
          name: selectedFile.name,
          size: selectedFile.size,
          uploadedAt: new Date().toISOString(),
          assignedUser: null,
          transcription_status: userData.transcription_status || 'processing',
          splitting_status: userData.splitting_status || 'processing'
        };
        const updatedVideos = [newVideo, ...uploadedVideos];
        setUploadedVideos(updatedVideos);
        saveUploadedVideos(updatedVideos);
      }
      
      // Reset form
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadPaused(false);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    } catch (error) {
      if (error.message === 'Upload cancelled') {
        // Upload was cancelled, don't show error or set paused state
        // The cancelUpload function already handled the state reset
      } else if (error.message !== 'Upload paused') {
        toast.error('Upload Failed');
        setUploadPaused(false);
      }
    } finally {
      setUploading(false);
      setUploadCancelToken(null);
    }
  };

  const pauseUpload = () => {
    if (uploadCancelToken) {
      uploadCancelToken.cancel('Upload paused by user');
      setUploadPaused(true);
    }
  };

  const cancelUpload = () => {
    if (uploadCancelToken) {
      uploadCancelToken.cancel('Upload cancelled by user');
    }
    // Complete reset of all states
    clearResumeData();
    setUploading(false);
    setUploadPaused(false);
    setUploadProgress(0);
    setSelectedFile(null);
    setIsProcessing(false);
    setUploadCancelToken(null);
    if (fileInput.current) {
      fileInput.current.value = '';
    }
    // Add toast notification for better UX
    toast.info('Upload cancelled');
  };

  const resumeUpload = async () => {
    if (!resumeData || !selectedFile) return;
    
    setUploading(true);
    setUploadPaused(false);
    
    try {
      // First, check the actual upload status from GCS to get the correct resume point
      let resumeFromByte = resumeData.uploadedBytes || 0;
      
      if (resumeData.uploadUrl) {
        const actualUploadedBytes = await getUploadStatus(resumeData.uploadUrl);
        
        // Use the actual bytes uploaded as confirmed by GCS
        resumeFromByte = actualUploadedBytes;
        
        // Update our local resume data with the correct byte count
        const updatedResumeData = {
          ...resumeData,
          uploadedBytes: actualUploadedBytes
        };
        saveResumeData(updatedResumeData);
        
        // Update progress display
        const percent = Math.round((actualUploadedBytes * 100) / selectedFile.size);
        setUploadProgress(percent);
      }
      
      const result = await uploadFileToGCS(selectedFile, resumeFromByte, resumeData.uploadUrl);
      
      if (result.url) {
        navigator.clipboard.writeText(result.url).catch(err => console.error('Failed to copy URL:', err));
        toast.success('Resume Upload Completed!');
      } else {
        toast.success('Resume upload completed successfully!');
      }
      
      // Only add to localStorage if user_id is null (video not assigned)
      const statusResponse = result.statusResponse;
      
      // Check if user_id is null (access from data property if response is nested)
      const userData = statusResponse?.data || statusResponse;
      
      if (userData && userData.user_id === null) {
        const newVideo = {
          filename: result.filename,
          name: selectedFile.name,
          size: selectedFile.size,
          uploadedAt: new Date().toISOString(),
          assignedUser: null,
          transcription_status: userData.transcription_status || 'processing',
          splitting_status: userData.splitting_status || 'processing'
        };
        const updatedVideos = [newVideo, ...uploadedVideos];
        setUploadedVideos(updatedVideos);
        saveUploadedVideos(updatedVideos);
      }
      
      // Reset form
      setSelectedFile(null);
      setUploadProgress(0);
      setUploadPaused(false);
      if (fileInput.current) {
        fileInput.current.value = '';
      }
    } catch (error) {
      setIsProcessing(false); // Reset processing state on error
      if (error.message === 'Upload cancelled') {
        // Upload was cancelled, don't show error or set paused state
        // The cancelUpload function already handled the state reset
      } else if (error.message !== 'Upload paused') {
        toast.error('Resume Upload Failed');
        setUploadPaused(false);
      }
    } finally {
      setUploading(false);
      setUploadCancelToken(null);
    }
  };

  const canResumeCurrentFile = selectedFile && resumeData && 
    resumeData.file.name === selectedFile.name && 
    resumeData.file.size === selectedFile.size && 
    resumeData.uploadedBytes > 0 && 
    !uploading;

  const isUploadInProgress = uploading || uploadPaused;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Upload Videos</h2>
        <p className="text-muted-foreground">Upload videos with resumable functionality. Manage assignments in the Video Library.</p>
      </div>

      {/* Upload Section */}
      <div className="bg-background border border-secondary-border rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Select Video File</h3>
        
        <input 
          type="file" 
          ref={fileInput} 
          accept="video/*" 
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <div 
          onClick={() => fileInput.current.click()}
          className="border-2 border-dashed border-secondary-border rounded-lg p-8 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all duration-200 mb-6"
        >
          <div className="text-center">
            <Video className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-foreground font-medium text-lg mb-2">
              {selectedFile ? selectedFile.name : 'Click to select a video file'}
            </p>
            {selectedFile && (
              <p className="text-sm text-primary mt-2 font-medium">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB selected
              </p>
            )}
          </div>
        </div>

        {/* File Info & Resume Notification */}
        {selectedFile && (
          <div className="mb-6 p-4 bg-secondary/50 rounded-lg">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-foreground">
                  <span className="font-medium">File:</span> {selectedFile.name}
                </p>
                <p className="text-sm text-foreground">
                  <span className="font-medium">Size:</span> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
              {canResumeCurrentFile && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 font-medium">Resume Available</p>
                  <p className="text-xs text-yellow-600">
                    {uploadProgress}% was uploaded previously
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Progress */}
        {selectedFile && (isUploadInProgress || uploadProgress > 0 || isProcessing) && (
          <div className="mb-6">
            <div className="flex justify-between text-sm text-foreground mb-2">
              <span className="font-medium">
                {uploading ? 'Uploading...' : uploadPaused ? 'Upload Paused' : canResumeCurrentFile ? 'Ready to resume' : 'Upload complete'}
              </span>
              <span className="font-mono">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-secondary-border rounded-full h-3 mb-2">
              <div 
                className={`h-3 rounded-full transition-all duration-300 ${
                  uploadPaused ? 'bg-yellow-500' : uploadProgress === 100 ? 'bg-green-500' : 'bg-primary'
                }`}
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            {uploadPaused && (
              <p className="text-sm text-yellow-600 text-center bg-yellow-50 rounded p-2">
                Upload paused at {uploadProgress}% - click Resume to continue
              </p>
            )}
          </div>
        )}

        {/* Upload Actions */}
        <div className="flex flex-wrap gap-3">
          {!uploading && !canResumeCurrentFile && !isProcessing && (
            <button 
              onClick={uploadVideo} 
              disabled={!selectedFile}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 ${
                !selectedFile
                  ? 'bg-secondary text-muted-foreground cursor-not-allowed'
                  : 'bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg'
              }`}
            >
              <Upload className="inline h-5 w-5 mr-2" />
              Upload Video
            </button>
          )}

          {canResumeCurrentFile && !uploading && !isProcessing && (
            <>
              <button 
                onClick={resumeUpload}
                className="px-6 py-3 rounded-lg font-semibold bg-primary hover:bg-primary/90 text-white transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <Play className="inline h-5 w-5 mr-2" />
                Resume Upload ({uploadProgress}%)
              </button>
              <button 
                onClick={clearResumeData}
                className="px-6 py-3 rounded-lg font-semibold bg-secondary hover:bg-secondary/80 text-foreground transition-all duration-200"
              >
                Start Over
              </button>
            </>
          )}

          {uploading && (
            <>
              <button 
                onClick={pauseUpload}
                className="px-6 py-3 rounded-lg font-semibold bg-yellow-500 hover:bg-yellow-600 text-white transition-all duration-200"
              >
                <Pause className="inline h-5 w-5 mr-2" />
                Pause Upload
              </button>
              <button 
                onClick={cancelUpload}
                className="px-6 py-3 rounded-lg font-semibold bg-red-500 hover:bg-red-600 text-white transition-all duration-200"
              >
                <X className="inline h-5 w-5 mr-2" />
                Cancel Upload
              </button>
            </>
          )}
        </div>

        {/* Success Message */}
        {uploadProgress === 100 && !uploading && !isProcessing && !uploadPaused && selectedFile && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-800 font-medium">✅ Upload Completed Successfully!</p>
            <p className="text-green-600 text-sm mt-1">Your video is now available in the Video Library for assignment to clients.</p>
          </div>
        )}
      </div>
    </div>
  );
} 